import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useBackendAuth } from '../auth/BackendAuthProvider'
import SettingsDropdown from './SettingsDropdown'

export const Layout = ({ children, activeView, setActiveView }) => {
  return (
    <div className="min-h-screen bg-brand-contentBg">
      <Header activeView={activeView} setActiveView={setActiveView} />
      <main className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          {children}
        </motion.div>
      </main>
    </div>
  )
}

export const Header = ({ activeView, setActiveView }) => {
  const { user } = useBackendAuth();
  const { t } = useTranslation('dashboard');
  const [quotationStats, setQuotationStats] = React.useState(null);

  React.useEffect(() => {
    const fetchQuotationStats = async () => {
      try {
        const { dataService } = await import('../../services/DataService');
        const result = await dataService.getQuotationsPaginated(1, 5);
        if (result.success) {
          setQuotationStats({
            totalCount: result.totalCount,
            recentQuotations: result.data.slice(0, 3)
          });
        }
      } catch (error) {
        console.error('Failed to fetch quotation stats:', error);
      }
    };

    if (user) {
      fetchQuotationStats();
    }
  }, [user]);

  return (
    <div className="relative">
      <motion.header
        className="bg-brand-headerBg relative overflow-visible z-10"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container mx-auto px-6 py-4">
          {/* Main Header Bar with Logo, Title, Navigation, and User Info */}
          <div className="flex items-center justify-between">
            {/* Left: Logo and Title */}
            <div className="flex items-center relative">
              {/* Logo container that extends below */}
              <div className="relative flex items-center" style={{width: '100px', height: '48px'}}>
                {/* Overlay logo positioned here */}
                <div className="absolute top-0 left-0 z-30 bg-white rounded-sm overflow-hidden" style={{width: '100px', height: '100px', transform: 'translateY(-10px)'}}>
                  <img
                    src="https://statics.solerpalau.com/skin/frontend/solerpalau/default/images/svg/logo.svg"
                    alt="Soler & Palau"
                    className="w-full h-full object-cover object-center border-0"
                    style={{width: '100px', height: '100px', border: 'none', outline: 'none'}}
                    onError={(e) => {
                      console.log('Image failed to load:', e);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div
                    className="w-full h-full bg-brand-primary flex items-center justify-center text-white text-xl font-bold"
                    style={{display: 'none'}}
                  >
                    S&P
                  </div>
                </div>
              </div>

              <div className="ml-20">
                <h1 className="text-3xl font-bold text-brand-headerText">{t('header.title')}</h1>
              </div>
            </div>

          {/* Center: Navigation Tabs */}
          {activeView && setActiveView && (
            <nav className="flex space-x-6">
              <button
                onClick={() => setActiveView('overview')}
                className={`px-4 py-2 transition-all font-bold uppercase tracking-wider text-sm ${
                  activeView === 'overview'
                    ? 'text-brand-headerText bg-brand-primary rounded-sm'
                    : 'text-brand-headerText hover:text-gray-300'
                }`}
              >
                {t('nav.overview')}
              </button>
              <button
                onClick={() => setActiveView('list')}
                className={`px-4 py-2 transition-all font-bold uppercase tracking-wider text-sm ${
                  activeView === 'list'
                    ? 'text-brand-headerText bg-brand-primary rounded-sm'
                    : 'text-brand-headerText hover:text-gray-300'
                }`}
              >
                {t('nav.allQuotes')}
              </button>
              <button
                onClick={() => setActiveView('create')}
                className={`px-4 py-2 transition-all font-bold uppercase tracking-wider text-sm ${
                  activeView === 'create'
                    ? 'text-brand-headerText bg-brand-primary rounded-sm'
                    : 'text-brand-headerText hover:text-gray-300'
                }`}
              >
                {t('nav.createQuote')}
              </button>
            </nav>
          )}

          {/* Right: User Info */}
          <div className="flex items-center space-x-4">
            {user && (
              <>
                <span className="text-sm text-brand-headerText">
                  {user.name || user.email}
                </span>
                <SettingsDropdown />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Gray content area with space for logo overlay */}
      <div className="bg-brand-contentBg relative">
        <div className="container mx-auto px-6 py-3">
          {/* Space for logo overlay */}
          <div style={{height: '30px'}}></div>
        </div>
      </div>
      </motion.header>

      {/* Logo is now integrated within the flex layout above */}
    </div>
  )
}

export const Container = ({ children, className = '' }) => {
  return (
    <div className={`max-w-7xl mx-auto ${className}`}>
      {children}
    </div>
  )
}
